import { RequestAnalysisStatus } from "../../models/intake";

/**
 * Pure decision helper for the intake-analysis progress/outcome announcement
 * (DataEntryDetails.updateIntakeAnalysisToast).
 *
 * Extracted into its own module (imports only the RequestAnalysisStatus enum) so the
 * routing decision can be unit-tested in isolation, deliberately avoiding
 * DataEntryDetails' heavy, node:stream-tainted import chain (services/Network etc.)
 * that prevents a mounted-component test suite from loading locally - same rationale
 * as intakeFetchGate.ts and intakeAutoTrigger.ts.
 *
 * Two bugs shaped this:
 *
 * AB#35101 - Classic Mode must receive NO analysis messages at all. Classic is manual
 * data entry: a specialist typing every field themselves has nothing to do with an
 * "Analyzing the request document…" notice, and AB#34102's original decision to show
 * the toast in both modes ("so the specialist always sees that AI processing is
 * happening even in Classic Mode") is superseded by that report. Note this costs no
 * feedback anywhere: with the QuickEntry flag OFF, Classic's own Extract button owns
 * its progress spinner and results dialog (DataEntryDetailsBody, intakeVisible - which
 * itself requires !QuickEntry); with the flag ON, that button doesn't render and
 * extraction feedback belongs to the Quick Entry pane.
 *
 * AB#35091 - the failure message promised Classic Mode ("You can proceed with manual
 * data entry (Classic Mode)") while the app stayed in Quick Entry. Message and behavior
 * now agree, split by whether the failure is retryable:
 *   - RetryExceeded is terminal - the extractor will not try again, so Quick Entry can
 *     never populate. Hand off to Classic Mode (the one-way classicModeOverride the
 *     "Leave Quick Entry" exit already uses) and say so.
 *   - Any other failure (the reported >100s timeout / 500) is still retryable by
 *     re-attaching the request document. Auto-routing there would be worse than the
 *     bug: classicModeOverride is one-way and, with the QuickEntry flag on, Classic has
 *     no Extract button - so a single transient timeout would permanently strand the
 *     transaction in manual entry. Stay in Quick Entry and offer both real options
 *     instead of implying an automatic switch.
 */
export type IntakeAnalysisOutcome =
    /** Nothing changed this render - announce nothing. */
    | "none"
    /** Not in Quick Entry: announce nothing, and clear any notice left over from a mid-analysis switch. */
    | "suppressed"
    /** Extraction just started. */
    | "started"
    /** Extraction resolved with data. */
    | "succeeded"
    /** Extraction failed but can be retried by re-attaching the document. */
    | "failedRetryable"
    /** Extraction failed terminally (retries exhausted) - hand off to Classic Mode. */
    | "failedTerminal";

export interface IntakeAnalysisOutcomeInputs {
    /** DataEntryDetails.isQuickEntryEligible() - whether the Quick Entry pane is the surface being rendered. */
    readonly quickEntryActive: boolean;
    /** Either intake fetch was in flight on the previous render. */
    readonly wasFetching: boolean;
    /** Either intake fetch is in flight on this render. */
    readonly isFetching: boolean;
    /** Either intake fetch reported success (API_SUCCESS). */
    readonly fetchSucceeded: boolean;
    /** requestAnalysis?.data != null - an extraction payload actually came back. */
    readonly hasExtractedData: boolean;
    /** requestAnalysis?.status - RetryExceeded is the only terminal failure. */
    readonly analysisStatus?: RequestAnalysisStatus | null;
    /**
     * AB#35121: true when an analysis is present in the slice but belongs to a DIFFERENT
     * transaction than the one on screen.
     *
     * The intake slice holds one analysis with no transaction key, and navigation does not cancel
     * an extraction already in flight (the POST and GET sit under separate `takeLatest` watchers).
     * So a response for transaction A can land while B is displayed. Ownership is decided from the
     * payload itself - RequestAnalysis carries facilityId + requestBufferId - via
     * analysisBelongsToTransaction, so this needs no instance state and no observation of fetch
     * transitions to get right.
     *
     * False when there is no analysis at all: ownership is unknowable then, and a failure with no
     * payload (a service error, say) is still this transaction's failure to report.
     */
    readonly foreignAnalysis?: boolean;
    readonly hasRequestDocument?: boolean;
    /**
     * `getTransactionDocumentsAPIStatus === API_SUCCESS` - i.e. the documents list has loaded for
     * the MOST RECENTLY LOADED transaction, which on a transaction-change render is still the
     * previous one (initialAPICalls, which re-dispatches getTransactionDocuments, runs later in
     * componentDidUpdate than the announcement does). Reviewed on !9636 (finding 6): the earlier
     * wording claimed "for THIS transaction", an invariant the component does not provide.
     * It is a sanity check on the document guard below, not a per-transaction guarantee - the
     * ownership check is what makes cross-transaction attribution safe.
     */
    readonly documentsLoaded?: boolean;
}

export function classifyIntakeAnalysisOutcome(inputs: IntakeAnalysisOutcomeInputs): IntakeAnalysisOutcome {
    const { quickEntryActive, wasFetching, isFetching } = inputs;
    if (!quickEntryActive) {
        // A notice can only be on screen if a fetch was, or still is, running -
        // limiting the dismiss to that window keeps this from dispatching a
        // pointless toast-store update on every unrelated re-render.
        return wasFetching || isFetching ? "suppressed" : "none";
    }
    if (!wasFetching && isFetching) {
        return "started";
    }
    if (wasFetching && !isFetching) {
        // AB#35121: a result belonging to another transaction must not speak for the one now on
        // screen. Clear any notice and say nothing.
        if (inputs.foreignAnalysis) {
            return "suppressed";
        }
        if (inputs.fetchSucceeded && inputs.hasExtractedData) {
            return "succeeded";
        }
        // AB#35121: nothing to analyse means no failure to report. This is the shape that made
        // "the new transaction switched to Classic on its own" possible. Gated on the documents
        // list having LOADED - an empty list mid-load means "unknown", not "none", and
        // suppressing on that would hide a genuine failure.
        if (inputs.documentsLoaded === true && inputs.hasRequestDocument === false) {
            return "suppressed";
        }
        return inputs.analysisStatus === RequestAnalysisStatus.RetryExceeded
            ? "failedTerminal"
            : "failedRetryable";
    }
    return "none";
}
